import { useEffect, useLayoutEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

// A lightweight spotlight tour over the real sidebar navigation.
// Highlights each nav item in turn with a floating explainer card.
const STOPS = [
  { testid: "nav-today", title: "Today", text: "Your daily read — decisions waiting, items due and new site photos across your jobs." },
  { testid: "nav-projects", title: "Projects", text: "Every job lives here. Open one to find its Build, Money and Design tabs." },
  { testid: "nav-schedule", title: "Schedule", text: "The timeline across all projects — list, week, month or Gantt, by crew or trade." },
  { testid: "nav-learn", title: "Learn", text: "This Learning Centre — glossary, study guide, Q&A and the walkthrough, always here." },
];

function rectOf(testid) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return r;
}

export default function GuidedTour({ onClose }) {
  const stops = STOPS.filter((s) => rectOf(s.testid));
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const stop = stops[i];

  useLayoutEffect(() => {
    if (!stop) return;
    const update = () => setRect(rectOf(stop.testid));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [stop]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!stops.length) {
    return (
      <div className="fixed inset-0 z-[70] grid place-items-center bg-stone-950/70 p-6" data-testid="guided-tour-overlay" onClick={onClose}>
        <div className="max-w-sm rounded-2xl bg-card p-6 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
          <p className="text-sm text-muted-foreground">The guided tour highlights the sidebar, which is available on a larger screen. On mobile, use the narrated walkthrough instead.</p>
          <Button className="mt-4 rounded-full" onClick={onClose} data-testid="guided-tour-close">Got it</Button>
        </div>
      </div>
    );
  }

  const pad = 6;
  const ring = rect && { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 };
  const cardLeft = rect ? Math.min(window.innerWidth - 320, rect.right + 16) : 16;
  const cardTop = rect ? Math.max(16, rect.top) : 16;
  const last = stops.length - 1;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="guided-tour-overlay">
      <div className="absolute inset-0 bg-stone-950/55" onClick={onClose} />
      {ring && (
        <div className="pointer-events-none absolute rounded-lg ring-2 ring-white transition-all duration-300"
          style={{ top: ring.top, left: ring.left, width: ring.width, height: ring.height, boxShadow: "0 0 0 9999px rgba(12,10,9,0.55)" }} data-testid="guided-tour-ring" />
      )}
      <div className="absolute w-[300px] rounded-2xl bg-card p-5 shadow-2xl" style={{ top: cardTop, left: cardLeft }} data-testid={`guided-tour-stop-${stop.testid}`}>
        <div className="flex items-start justify-between">
          <p className="eyebrow">Step {i + 1} of {stops.length}</p>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="guided-tour-x" aria-label="Close tour"><X className="h-4 w-4" /></button>
        </div>
        <h3 className="mt-1.5 font-serif text-xl">{stop.title}</h3>
        <p className="mt-1.5 text-sm text-muted-foreground">{stop.text}</p>
        <div className="mt-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" disabled={i === 0} onClick={() => setI((n) => Math.max(0, n - 1))} data-testid="guided-tour-prev">Back</Button>
          {i < last
            ? <Button size="sm" className="rounded-full" onClick={() => setI((n) => n + 1)} data-testid="guided-tour-next">Next</Button>
            : <Button size="sm" className="rounded-full" onClick={onClose} data-testid="guided-tour-done">Done</Button>}
        </div>
      </div>
    </div>
  );
}
